import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { authHelper } from '../utils/authHelper';

const API = process.env.REACT_APP_BACKEND_URL;

function ExamInterface() {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExam();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  useEffect(() => {
    if (exam && timeLeft > 0 && !submitted) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, timeLeft, submitted]);

  const loadExam = async () => {
    try {
      const res = await fetch(`${API}/api/lms/exams/${examId}`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      setExam(data);
      setTimeLeft(data.time_limit || 3600);
    } catch (err) {
      console.error('Error loading exam:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const handleImageUpload = async (questionId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = authHelper.getToken();
      const res = await fetch(`${API}/api/uploads/image`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData
      });

      const data = await res.json();
      
      if (data.url) {
        setAnswers({
          ...answers,
          [questionId]: data.url
        });
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Error al subir la imagen. Intenta nuevamente.');
    }
  };

  const handleSubmitExam = async () => {
    if (submitting || submitted) return;

    // Validate all questions answered
    const unanswered = exam.questions.filter(q => !answers[q.question_id]);
    if (unanswered.length > 0) {
      if (!window.confirm(`Tienes ${unanswered.length} preguntas sin responder. ¿Enviar de todas formas?`)) {
        return;
      }
    }

    setSubmitting(true);

    try {
      // Format answers
      const formattedAnswers = exam.questions.map(q => {
        const answerValue = answers[q.question_id] || '';
        
        if (q.type === 'image_upload') {
          return {
            question_id: q.question_id,
            answer: '',
            image_url: answerValue
          };
        } else {
          return {
            question_id: q.question_id,
            answer: answerValue
          };
        }
      });

      const res = await fetch(`${API}/api/lms/exams/submit`, {
        method: 'POST',
        headers: {
          ...authHelper.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exam_id: examId,
          answers: formattedAnswers
        })
      });

      const data = await res.json();
      
      if (data.success) {
        setSubmitted(true);
        // Wait a bit for grading
        setTimeout(() => {
          loadResult(data.submission_id);
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting exam:', err);
      alert('Error al enviar el examen. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const loadResult = async (submissionId) => {
    try {
      const res = await fetch(`${API}/api/lms/exams/submissions/${submissionId}`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      console.error('Error loading result:', err);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <p className="text-white text-xl">Cargando examen...</p>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <p className="text-white text-xl">Examen no encontrado</p>
      </div>
    );
  }

  // Show results
  if (submitted && result) {
    const passed = result.status === 'passed';
    
    return (
      <div className="min-h-screen bg-[#02040A] p-6">
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-lg p-8 mb-6 ${passed ? 'bg-green-900 border-2 border-green-500' : 'bg-red-900 border-2 border-red-500'}`}>
            <div className="text-center">
              <span className="text-7xl block mb-4">{passed ? '🎉' : '📚'}</span>
              <h1 className="text-3xl font-bold text-white mb-2">
                {passed ? '¡Felicitaciones! Aprobaste' : 'Resultado del Examen'}
              </h1>
              <p className="text-xl text-gray-200 mb-6">
                Calificación: <strong className="text-3xl">{result.percentage}%</strong>
              </p>
              <p className="text-gray-300">
                {passed 
                  ? `Superaste el puntaje mínimo de ${exam.passing_score}%`
                  : `Necesitas ${exam.passing_score}% para aprobar. Sigue estudiando.`
                }
              </p>
            </div>
          </div>

          {/* Detailed Feedback */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-6">📝 Retroalimentación Detallada</h2>
            
            {exam.questions.map((question, idx) => {
              const feedback = result.ai_feedback[question.question_id];
              if (!feedback) return null;

              return (
                <div key={question.question_id} className="mb-6 pb-6 border-b border-gray-700 last:border-0">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-white">
                      Pregunta {idx + 1}: {question.text}
                    </h3>
                    <span className={`px-3 py-1 rounded font-semibold ${
                      feedback.score >= question.points * 0.7 
                        ? 'bg-green-900 text-green-300' 
                        : 'bg-red-900 text-red-300'
                    }`}>
                      {feedback.score}/{question.points} pts
                    </span>
                  </div>

                  <div className="bg-gray-900 rounded p-4">
                    <p className="text-sm text-gray-400 mb-2">Retroalimentación de IA:</p>
                    <p className="text-gray-300">{feedback.feedback}</p>
                  </div>
                </div>
              );
            })}

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => window.location.href = '#/lms'}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Volver a Cursos
              </button>
              {!passed && (
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
                >
                  Reintentar Examen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show grading screen
  if (submitted && !result) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">🤖</div>
          <h2 className="text-2xl font-bold text-white mb-2">Calificando con IA...</h2>
          <p className="text-gray-400">
            Nuestro sistema de inteligencia artificial está evaluando tus respuestas.
          </p>
          <p className="text-gray-500 text-sm mt-2">Esto puede tomar unos segundos...</p>
        </div>
      </div>
    );
  }

  // Show exam
  return (
    <div className="min-h-screen bg-[#02040A] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{exam.title}</h1>
              <p className="text-gray-400">{exam.description}</p>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${timeLeft < 300 ? 'text-red-400' : 'text-[#C5A14C]'}`}>
                ⏱️ {formatTime(timeLeft)}
              </div>
              <p className="text-sm text-gray-500">Tiempo restante</p>
            </div>
          </div>

          <div className="flex gap-6 text-sm text-gray-400">
            <span>📊 {exam.total_points} puntos totales</span>
            <span>✅ {exam.passing_score}% para aprobar</span>
            <span>📝 {exam.questions.length} preguntas</span>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {exam.questions.map((question, idx) => (
            <div key={question.question_id} className="bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white">
                  Pregunta {idx + 1}
                </h3>
                <span className="px-3 py-1 bg-[#C5A14C] text-white rounded text-sm font-semibold">
                  {question.points} pts
                </span>
              </div>

              <p className="text-gray-300 mb-4">{question.text}</p>

              {/* Multiple Choice */}
              {question.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {question.options.map((option, optIdx) => (
                    <label
                      key={`${question.question_id}-opt-${option}`}
                      className="flex items-center p-3 bg-gray-900 rounded hover:bg-gray-750 cursor-pointer transition-colors"
                    >
                      <input
                        type="radio"
                        name={`question_${question.question_id}`}
                        value={option}
                        onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                        className="mr-3"
                      />
                      <span className="text-white">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Text Answer */}
              {question.type === 'text' && (
                <textarea
                  rows="5"
                  placeholder="Escribe tu respuesta aquí..."
                  value={answers[question.question_id] || ''}
                  onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded border border-gray-700 focus:border-[#C5A14C] outline-none"
                />
              )}

              {/* Image Upload */}
              {question.type === 'image_upload' && (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(question.question_id, e.target.files[0]);
                      }
                    }}
                    className="hidden"
                    id={`file_${question.question_id}`}
                  />
                  <label
                    htmlFor={`file_${question.question_id}`}
                    className="block w-full p-6 bg-gray-900 border-2 border-dashed border-gray-700 rounded text-center cursor-pointer hover:border-[#C5A14C] transition-colors"
                  >
                    {answers[question.question_id] ? (
                      <div>
                        <img
                          src={answers[question.question_id]}
                          alt="Preview"
                          className="max-w-xs mx-auto mb-2 rounded"
                        />
                        <p className="text-green-400">✅ Imagen cargada - Click para cambiar</p>
                      </div>
                    ) : (
                      <div>
                        <span className="text-4xl block mb-2">📸</span>
                        <p className="text-gray-400">Click para subir imagen</p>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <div className="flex gap-4">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmitExam}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Enviando...' : '📤 Enviar Examen'}
            </button>
          </div>
          <p className="text-gray-500 text-sm mt-3 text-center">
            Asegúrate de revisar todas tus respuestas antes de enviar
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExamInterface;
